import { useState, useEffect, useMemo } from "react";
import {
  fetchCostOverviewDisplayOrder,
  getComponentSortKey,
  getFallbackSortOrder,
  getInvoiceLineSortKey,
  type CostOverviewOrderItem,
} from "../services/costOverviewDisplayOrderService";

type SortableCostItem = {
  componentType?: string;
  componentId?: number;
  name?: string;
  cost_type?: string;
  description?: string;
};

/**
 * Returns a sort function for cost overview / invoice lines.
 * Uses the same Kostenoverzicht volgorde as /admin/pricing.
 */
export function useCostOverviewDisplayOrder(
  pricingComponents?: { id: number; name: string }[]
): (a: SortableCostItem, b: SortableCostItem) => number {
  const [orderItems, setOrderItems] = useState<CostOverviewOrderItem[]>([]);

  useEffect(() => {
    fetchCostOverviewDisplayOrder().then(setOrderItems);
  }, []);

  const sortMap = useMemo(() => {
    const map = new Map<string, number>();
    orderItems.forEach((item) => {
      map.set(item.item_key, item.sort_order);
    });
    return map;
  }, [orderItems]);

  return (a, b) => {
    const isInvoiceLine =
      a.cost_type != null ||
      a.description != null ||
      b.cost_type != null ||
      b.description != null;

    const keyA = isInvoiceLine
      ? getInvoiceLineSortKey(
          {
            cost_type: a.cost_type ?? a.componentType,
            description: a.description ?? a.name,
          },
          { orderItems, pricingComponents }
        )
      : getComponentSortKey(a);
    const keyB = isInvoiceLine
      ? getInvoiceLineSortKey(
          {
            cost_type: b.cost_type ?? b.componentType,
            description: b.description ?? b.name,
          },
          { orderItems, pricingComponents }
        )
      : getComponentSortKey(b);

    const orderA = getFallbackSortOrder(keyA, sortMap);
    const orderB = getFallbackSortOrder(keyB, sortMap);
    if (orderA !== orderB) return orderA - orderB;
    return keyA.localeCompare(keyB);
  };
}
